import { GetServerSideProps } from 'next'
import { slugs } from '@/constants/slugs'
import { runExperimentOnServer } from '@/experimentation/server'
import { NextPageWithLayout } from '@/layouts'
import { PresalesLayout } from '@/layouts/PresalesLayout'
import { getServerSideClient } from '@/services/ApolloClient'
import { getProjectPhase } from '@/services/ProjectsService/ProjectsService'
import { getShopifyCollection, ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { PhaseSlug } from '@/types/codegen-federation'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getServerSidePropsErrorHandler, getUrlFromFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PreSalesGiveawayView } from '@/views/PreSales/PreSalesGiveawayView'

const COLLECTION_IDS = {
  [slugs.homestead]: 'gid://shopify/Collection/436194312423',
  [slugs.braveTheDark]: 'gid://shopify/Collection/438477455591',
  [slugs.ruleBreakers]: 'gid://shopify/Collection/439744889063',
  ['rule-breakers-tech']: 'gid://shopify/Collection/439744889063',
  [slugs.kingOfKings]: 'gid://shopify/Collection/443095941351',
  [slugs.theLastRodeo]: 'gid://shopify/Collection/443462025447',
}

interface PreSalesPageProps {
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[]
  phase: PhaseSlug | null
  newTicketPackExperiment: boolean
}

export const PreSalesPage: NextPageWithLayout<PreSalesPageProps> = ({
  tickets,
  theatricalSlug,
  phase,
  newTicketPackExperiment,
}) => {
  return (
    <div className="absolute inset-0 !top-0 !overflow-x-hidden bg-white lg:fixed">
      <PreSalesGiveawayView
        isGiveawayRoute
        theatricalSlug={theatricalSlug}
        tickets={tickets}
        phase={phase}
        newTicketPackExperiment={newTicketPackExperiment}
      />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params, res, req }) => {
  return getServerSidePropsErrorHandler(
    { resolvedUrl: getUrlFromFilePath('pages/[locale]/giveaway/[slug]/index.tsx', params) },
    async ({ next404 }) => {
      const locale = getLocaleFromParams(params)
      const theatricalSlug = params?.slug as string

      const client = getServerSideClient({ req, res })

      const collectionId = COLLECTION_IDS?.[theatricalSlug]

      if (!collectionId) {
        return next404()
      }

      const tickets = await getShopifyCollection({ collectionId })
      const phase = await getProjectPhase(client, theatricalSlug)

      const newTicketPackExperiment =
        theatricalSlug === slugs.kingOfKings || theatricalSlug === slugs.theLastRodeo
          ? await runExperimentOnServer('giveaway-new-ticket-selector-card', false, req, res)
          : false

      return {
        props: {
          tickets,
          theatricalSlug,
          phase,
          newTicketPackExperiment,
          ...(await loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account'])),
        },
      }
    },
  )
}

PreSalesPage.getLayout = (page) => {
  return <PresalesLayout isDarkMode={false}>{page}</PresalesLayout>
}
export default PreSalesPage
